import { PDFDocument, PDFName, PDFNumber, StandardFonts, rgb, setFillingColor, AnnotationFlags, beginText, endText, moveText, popGraphicsState, pushGraphicsState, setFontAndSize, showText, type PDFTextField, type PDFCheckBox, PDFDict, type PDFPage, type PDFFont, type AppearanceProviderFor } from 'pdf-lib';
import { type BuiltinAnswers, type EventDateRange, type PublicEvent } from '../../shared/types';
import { sortEventDates } from '../../shared/dates';
import { formatPhone } from '../../shared/phone';
import { ageOn, formatUsDate, participantName, participantWard } from '../../shared/builtin';
import { pdfAnswerFont, pdfHandFont, pdfSignatureFont } from './pdfFont';
import { PERMISSION_FIELDS as F } from './permissionFields';
import { SIGNATURE_LINE } from '../../shared/pdf/signatures';
import { pdfFirstBaseline, pdfFontMetrics } from '../../shared/pdf/layout';

const PDF_INK = rgb(104 / 255, 75 / 255, 115 / 255); // Site accent: #684b73
const MIN_TEXT_SIZE = 7;
const TEXT_SIZE = 9;
/** A multiline field's text too long for its usual spacing is stacked closer, in smaller writing, down to this size. */
const MIN_STACKED_SIZE = 5.5;
/** Stacked lines' spacing, in ems: just enough that one line's descenders clear the next line's capitals. */
const STACKED_LINE_HEIGHT = 1.1;

import type { SignatureInput } from '../../shared/types';
export type { SignatureInput } from '../../shared/types';

export interface FillOptions {
  template: Uint8Array;
  event: PublicEvent;
  answers: BuiltinAnswers;
  participantSignature?: SignatureInput;
  parentSignature?: SignatureInput;
  signedOn: string; // YYYY-MM-DD
  flatten: boolean;
}


export function formatEventDates(dates: EventDateRange[]): string {
  return sortEventDates(dates).map((d) => (d.end && d.end !== d.start ? `${formatUsDate(d.start)} – ${formatUsDate(d.end)}` : formatUsDate(d.start))).join(', ');
}

export function unitLine(ev: PublicEvent, answers: BuiltinAnswers): { ward: string; stake: string } {
  const ward = participantWard(ev, answers);
  return { ward: ward || (ev.level === 'stake' ? 'Stake activity' : ''), stake: ev.stakeName.replace(/\s+stake$/i, '') };
}

export async function fillPermissionPdf(o: FillOptions): Promise<Uint8Array> {
  // Keep the template's metadata so the same snapshot always produces the same bytes.
  const doc = await PDFDocument.load(o.template, { updateMetadata: false });
  // The official source has two optional FontWeight=350 descriptors. Omit invalid
  // hints in the generated copy (PDF 32000-1 table 122) to avoid reader warnings.
  for (const [, object] of doc.context.enumerateIndirectObjects()) {
    if (!(object instanceof PDFDict) || object.get(PDFName.of('Type'))?.toString() !== '/FontDescriptor') continue;
    const weight = object.get(PDFName.of('FontWeight'));
    if (weight && (!(weight instanceof PDFNumber) || ![100,200,300,400,500,600,700,800,900].includes(weight.asNumber()))) object.delete(PDFName.of('FontWeight'));
  }
  const form = doc.getForm();
  let answerFont = await doc.embedFont(StandardFonts.Helvetica);
  const answerTexts = [...Object.values(o.answers).filter((v): v is string => typeof v === 'string'), o.event.name, o.event.description, o.event.stakeName, o.event.wardName ?? '', o.event.leaderName, o.event.leaderEmail];
  const signatureTexts = [o.participantSignature, o.parentSignature].flatMap(s => s?.kind === 'typed' ? [s.name] : []);
  const supports = (font: PDFFont, texts: string[]) => {
    const characters = new Set(font.getCharacterSet());
    return texts.every(text => [...text].every(c => '\r\n\t'.includes(c) || characters.has(c.codePointAt(0)!)));
  };
  // Answers are written in the handwriting font, and typed signatures in the cursive font, when it has all their
  // characters, and in the answer font otherwise.
  const [{ default: fontkit }, handBytes, cursiveBytes] = await Promise.all([import('@pdf-lib/fontkit'), pdfHandFont(), signatureTexts.length ? pdfSignatureFont() : undefined]);
  doc.registerFontkit(fontkit);
  const handFont = await doc.embedFont(handBytes, { subset: true, features: { liga: false, clig: false, calt: false } });
  const cursiveFont = cursiveBytes && await doc.embedFont(cursiveBytes, { subset: true });
  let unicodeFont: PDFFont | undefined;
  const unicodeAnswers = !supports(answerFont, answerTexts.filter(text => !supports(handFont, [text]))), unicodeSignatures = signatureTexts.some(text => !cursiveFont || !supports(cursiveFont, [text]));
  if (unicodeAnswers || unicodeSignatures) {
    const fontBytes = await pdfAnswerFont();
    unicodeFont = await doc.embedFont(fontBytes, { subset: true });
    if (!supports(unicodeFont, [...answerTexts, ...signatureTexts])) throw new Error('The permission form contains characters the answer font cannot display.');
    if (unicodeAnswers) answerFont = unicodeFont;
  }
  const signatureFont = (signature: SignatureInput) => signature.kind === 'typed' && cursiveFont && supports(cursiveFont, [signature.name]) ? cursiveFont : unicodeFont ?? answerFont;
  const page1 = doc.getPage(0);
  const overflow: { label: string; text: string }[] = [];

  const firstDate = [...o.event.dates].map((d) => d.start).sort()[0] ?? o.signedOn;
  const age = ageOn(o.answers.dateOfBirth, firstDate);
  const a = o.answers;
  const u = unitLine(o.event, o.answers);

  const text = (name: string, value: string, label: string) => {
    const field = form.getTextField(name), font = supports(handFont, [value]) ? handFont : answerFont;
    const fits = fitsAt(field, value, MIN_TEXT_SIZE, font);
    let displayed = value;
    // A multiline field, such as the event description, that can't take the text at its usual spacing stacks the lines
    // closer together, in writing as small as it must be.
    if (!fits && field.isMultiline()) {
      // Paragraphs run on, so a blank line between them doesn't take one of the few lines there's room for.
      const runOn = value.trim().replace(/\s*\n\s*/g, ' ');
      let stacked = stackedLayout(field, runOn, font);
      if (!stacked) {
        const suffix = '… (see attached page)';
        // Cut in the smallest writing, which fits the most; no more can fit than fills every line there's room for.
        let low = 0, high = stackedCapacity(field, runOn, font);
        while (low < high) {
          const middle = Math.ceil((low + high) / 2);
          if (stackedLayout(field, runOn.slice(0, middle).trimEnd() + suffix, font, MIN_STACKED_SIZE)) low = middle;
          else high = middle - 1;
        }
        displayed = runOn.slice(0, low).trimEnd() + suffix;
        stacked = stackedLayout(field, displayed, font, MIN_STACKED_SIZE);
        overflow.push({ label, text: value });
      } else displayed = runOn;
      if (stacked) {
        field.setText(displayed);
        prepareWidgets(field, font, stacked.size);
        field.updateAppearances(font, stackedAppearance(stacked));
        return;
      }
    }
    if (!fits) {
      const suffix = '… (see attached page)';
      let low = 0, high = value.length;
      while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        if (fitsAt(field, value.slice(0, middle).trimEnd() + suffix, MIN_TEXT_SIZE, font)) low = middle;
        else high = middle - 1;
      }
      displayed = fitsAt(field, suffix, MIN_TEXT_SIZE, font) ? value.slice(0, low).trimEnd() + suffix : '…';
      overflow.push({ label, text: value });
    }
    field.setText(displayed);
    let size = TEXT_SIZE;
    while (size > MIN_TEXT_SIZE && !fitsAt(field, displayed, size, font)) size--;
    prepareWidgets(field, font, size);
    field.updateAppearances(font);
  };
  const check = (name: string, value: boolean | null) => setYesNo(form.getCheckBox(name), value);

  text(F.event, o.event.name, 'Event');
  text(F.dates, formatEventDates(o.event.dates), 'Date(s) of event');
  text(F.description, o.event.description, 'Describe event and activities');
  text(F.ward, u.ward, 'Ward');
  text(F.stake, u.stake, 'Stake');
  text(F.leader, o.event.leaderName, 'Event organizer');
  text(F.leaderPhone, formatPhone(o.event.leaderPhone), 'Event organizer phone number');
  text(F.leaderEmail, o.event.leaderEmail, 'Event organizer email');

  text(F.participant, participantName(a), 'Participant');
  text(F.dob, formatUsDate(a.dateOfBirth), 'Date of birth');
  text(F.age, age === null ? '' : String(age), 'Age');
  text(F.phone, formatPhone(a.telephone), 'Telephone number');
  text(F.address, a.address, 'Address');
  text(F.city, a.city, 'City');
  text(F.state, a.stateOrProvince, 'State or province');
  text(F.emergency, a.emergencyContact, 'Emergency contact');
  text(F.emergencyPrimary, formatPhone(a.emergencyPrimaryPhone), 'Primary telephone number');
  text(F.emergencySecondary, formatPhone(a.emergencySecondaryPhone), 'Secondary telephone number');

  check(F.specialDiet, a.specialDiet);
  text(F.dietExplanation, a.specialDiet ? a.dietExplanation : '', 'Dietary restrictions');
  check(F.allergies, a.allergies);
  text(F.allergyExplanation, a.allergies ? a.allergyExplanation : '', 'Allergies');
  text(F.medications, a.medications, 'Medications');
  check(F.selfAdmin, a.medications.trim() ? a.selfAdminister : null);
  check(F.chronicIllness, a.chronicIllness);
  text(F.chronicIllnessExplanation, a.chronicIllness ? a.chronicIllnessExplanation : '', 'Chronic or recurring illness');
  check(F.surgery, a.surgery);
  text(F.surgeryExplanation, a.surgery ? a.surgeryExplanation : '', 'Surgery or serious illness in the past year');
  text(F.otherLimitations, a.otherLimitations, 'Other limits, restrictions, or disabilities');
  text(F.specialNeeds, a.specialNeeds, 'Other accommodations or special needs');

  // Signatures: drawn over the signature lines; the text fields stay empty.
  const signedOn = formatUsDate(o.signedOn);
  if (o.participantSignature) {
    await drawSignature(doc, page1, form.getTextField(F.participantSignature), o.participantSignature, signatureFont(o.participantSignature), answerFont);
    text(F.date1, signedOn, 'Date');
  }
  if (o.parentSignature) {
    await drawSignature(doc, page1, form.getTextField(F.parentSignature), o.parentSignature, signatureFont(o.parentSignature), answerFont);
    text(F.date2, signedOn, 'Date');
  }

  // The template has a "Reset" button in the header; it must not appear on the signed copy.
  try { form.removeField(form.getButton(F.reset)); } catch { /* absent */ }

  if (overflow.length) addOverflowPage(doc, answerFont, o, overflow);

  if (o.flatten) {
    form.flatten();
    // Some template widgets are duplicated in page annotations; remove any leftovers or dangling references.
    for (const page of doc.getPages()) {
      const annotations = page.node.Annots();
      if (!annotations) continue;
      for (let i = annotations.size() - 1; i >= 0; i--) {
        const annotation = doc.context.lookup(annotations.get(i));
        if (!annotation || (annotation instanceof PDFDict && annotation.get(PDFName.of('Subtype')) === PDFName.of('Widget'))) annotations.remove(i);
      }
    }
    doc.catalog.delete(PDFName.of('AcroForm'));
  }
  return doc.save({ useObjectStreams: false });
}

/** A Yes/No pair is two checkbox widgets sharing one field; pdf-lib only knows how to tick the first, so set states directly. */
function setYesNo(cb: PDFCheckBox, value: boolean | null): void {
  const target = value === null ? 'Off' : value ? 'Yes' : 'No';
  cb.acroField.dict.set(PDFName.of('V'), PDFName.of(target));
  for (const w of cb.acroField.getWidgets()) {
    const on = w.getOnValue()?.decodeText();
    w.setAppearanceState(PDFName.of(on === target ? target : 'Off'));
  }
}

/** Widget-level appearances in the template must not override the chosen ink or size. */
function prepareWidgets(field: PDFTextField, font: PDFFont, size: number): void {
  for (const widget of field.acroField.getWidgets()) {
    widget.dict.delete(PDFName.of('DA'));
    widget.clearFlag(AnnotationFlags.Hidden); widget.clearFlag(AnnotationFlags.Invisible); widget.clearFlag(AnnotationFlags.NoView);
    widget.setFlag(AnnotationFlags.Print);
  }
  field.acroField.setDefaultAppearance(`${setFillingColor(PDF_INK)}\n/${font.name} ${size} Tf`);
}

interface StackedLayout { lines: string[]; size: number }

/** The largest size, from the smallest usual one down to the smallest stacked one, at which closely stacked lines of
 * `value` fit in the field from the tops of their capitals to the bottoms of their descenders; unset if none does. */
function stackedLayout(field: PDFTextField, value: string, font: PDFFont, largest = MIN_TEXT_SIZE): StackedLayout | undefined {
  const { width, height } = stackedBox(field), metrics = pdfFontMetrics(font);
  for (let size = largest; size >= MIN_STACKED_SIZE; size -= .25) {
    const room = stackedRoom(height, size, metrics);
    if (room < 1) continue;
    const lines = wrapLines(value, font, size, width, room);
    if (lines.length <= room && value.split(/\s+/).every(word => font.widthOfTextAtSize(word, size) <= width)) return { lines, size };
  }
}

const stackedBox = (field: PDFTextField) => { const r = field.acroField.getWidgets()[0].getRectangle(); return { width: r.width - 2, height: r.height - 2 }; };
/** How many lines at `size` stack in a box `height` tall. */
const stackedRoom = (height: number, size: number, metrics: { cap: number; descent: number }) => Math.floor((height / size - metrics.cap - metrics.descent) / STACKED_LINE_HEIGHT) + 1;
/** How many characters of `value` at most fit stacked in the field: as many as fill its lines in the smallest writing. */
function stackedCapacity(field: PDFTextField, value: string, font: PDFFont): number {
  const { width, height } = stackedBox(field), room = stackedRoom(height, MIN_STACKED_SIZE, pdfFontMetrics(font));
  return Math.min(value.length, wrapLines(value, font, MIN_STACKED_SIZE, width, room).slice(0, room).reduce((total, line) => total + line.length + 1, 0));
}

/** Draws a stacked layout on the field's bottom line, its last descenders 1 point above it, clear of the label above. */
function stackedAppearance({ lines, size }: StackedLayout): AppearanceProviderFor<PDFTextField> {
  return (_field, widget, font) => {
    const metrics = pdfFontMetrics(font), lineHeight = STACKED_LINE_HEIGHT * size;
    const baseline = 1 + pdfFirstBaseline({ lines, cap: metrics.cap * size, descent: metrics.descent * size, lineHeight }, widget.getRectangle().height - 2, 'bottom');
    return [pushGraphicsState(), beginText(), setFillingColor(PDF_INK), setFontAndSize(font.name, size), moveText(1, baseline),
      ...lines.flatMap((line, i) => [...(i ? [moveText(0, -lineHeight)] : []), showText(font.encodeText(line))]), endText(), popGraphicsState()];
  };
}

function fitsAt(field: PDFTextField, value: string, size: number, font: PDFFont): boolean {
  const widget = field.acroField.getWidgets()[0];
  const r = widget.getRectangle();
  const padding = (widget.getBorderStyle()?.getWidth() ?? 0) + 1;
  const width = r.width - padding * 2;
  const height = r.height - padding * 2;
  if (!field.isMultiline()) return !value.includes('\n') && font.widthOfTextAtSize(value, size) <= width && font.heightAtSize(size) <= height;
  const lines = wrapLines(value, font, size, width);
  return lines.length * font.heightAtSize(size) * 1.2 <= height && value.split(/\s+/).every(word => font.widthOfTextAtSize(word, size) <= width);
}

/** Wrap from the start, including long unbroken words, without repeatedly shaping a whole long answer. Stops once there
 * are more than `limit` lines, when only whether the text fits matters. */
function wrapLines(text: string, font: PDFFont, size: number, maxWidth: number, limit = Infinity): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split(/\r?\n/)) {
    let line = '';
    for (const word of paragraph.split(/ +/)) {
      const next = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(next, size) <= maxWidth) { line = next; continue; }
      if (line) { lines.push(line); line = ''; }
      for (const character of word) {
        if (line && font.widthOfTextAtSize(line + character, size) > maxWidth) { lines.push(line); line = ''; }
        line += character;
      }
      if (lines.length > limit) return lines;
    }
    lines.push(line);
  }
  return lines;
}

async function drawSignature(doc: PDFDocument, page: PDFPage, field: PDFTextField, sig: SignatureInput, font: PDFFont, labels: PDFFont): Promise<void> {
  const r = field.acroField.getWidgets()[0].getRectangle();
  if (sig.kind === 'drawn') {
    // The printed caption sits in the top-left of the field box, so the signature goes to its right, its line on the
    // baseline a typed signature sits on, up to 22 points above it.
    const img = await doc.embedPng(sig.png);
    const left = r.x + 200;
    const maxW = r.x + r.width - left - 4;
    const scale = Math.min(maxW / img.width, 22 / (img.height * SIGNATURE_LINE));
    const w = img.width * scale, h = img.height * scale;
    page.drawImage(img, { x: left, y: r.y + 2 - h * (1 - SIGNATURE_LINE), width: w, height: h });
  } else {
    const maxWidth = r.width - 264;
    const size = Math.min(13, maxWidth / font.widthOfTextAtSize(sig.name, 1));
    page.drawText(sig.name, { x: r.x + 200, y: r.y + 2, size, font, color: PDF_INK });
    page.drawText('(typed signature)', { x: r.x + r.width - 60, y: r.y + 3, size: 5, font: labels, color: rgb(0.4, 0.4, 0.4) });
  }
}

function addOverflowPage(doc: PDFDocument, answerFont: PDFFont, o: FillOptions, items: { label: string; text: string }[]): void {
  const margin = 54;
  let page = doc.addPage([612, 792]);
  let y = 792 - margin;
  const line = (text: string, size: number, heading = false) => {
    const font = answerFont;
    const lines = wrapLines(text, font, size, 612 - margin * 2);
    const lineHeight = Math.max(size * 1.35, font.heightAtSize(size) * 1.2);
    for (const row of lines) {
      if (y - lineHeight < margin) {
        page = doc.addPage([612, 792]); y = 792 - margin;
        page.drawText('Additional Information (continued)', { x: margin, y, size: 12, font: answerFont }); y -= 26;
      }
      page.drawText(row, { x: margin, y, size, font, color: heading ? rgb(0, 0, 0) : PDF_INK });
      y -= lineHeight;
    }
    if (heading) y -= 2;
  };
  line('Permission and Medical Release Form – Additional Information', 14, true);
  line(`${participantName(o.answers)} · ${o.event.name} · ${formatEventDates(o.event.dates)}`, 11);
  y -= 10;
  for (const item of items) {
    line(item.label, 11, true);
    for (const paragraph of item.text.split(/\n+/)) line(paragraph, 11);
    y -= 8;
  }
}

/** Render every page of a PDF to canvases (for on-screen preview). */
export async function renderPdf(bytes: Uint8Array, scale = 1.5): Promise<HTMLCanvasElement[]> {
  const pdfjs = await (await import('../pdf/pdfjs')).loadPdfjs();
  const pdf = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  const out: HTMLCanvasElement[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width; canvas.height = viewport.height;
    await page.render({ canvas, viewport }).promise;
    out.push(canvas);
  }
  return out;
}
